import { useState, useEffect, useRef } from "react";
import { Loader2, Search, UserPlus, Play, Send, Copy, Video, Stethoscope, X, History, FileText, Volume2, VolumeX, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_BADGE = {
  confirme: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  en_cours: { label: "En cours", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  termine: { label: "Terminée", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  annule: { label: "Annulée", cls: "bg-red-50 text-red-700 border-red-200" },
};

const emptyForm = { name: "", email: "", phone: "", dob: "", motif: "", start: "", duration_min: 30 };

export function TeleconsultationManager({ appts = [], reload, stats }) {
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [history, setHistory] = useState(null); // {name, sessions} | "loading" | null
  const [logOpen, setLogOpen] = useState(null);

  const teleconsults = appts.filter((a) => a.type === "teleconsultation");
  const waitingList = teleconsults
    .filter((a) => a.patient_present && a.status !== "termine")
    .sort((a, b) => new Date(a.patient_joined_at || 0) - new Date(b.patient_joined_at || 0));
  const waitingCount = waitingList.length;

  // Seuils d'alerte réglables (salle d'attente)
  const [thresholds, setThresholds] = useState({ orange: 10, red: 20 });
  const [thDraft, setThDraft] = useState({ orange: 10, red: 20 });
  const [thSaving, setThSaving] = useState(false);
  const crDaysRef = useRef(7);
  useEffect(() => {
    api.get("/admin/visio/settings").then(({ data }) => {
      if (data.cr_link_days) crDaysRef.current = data.cr_link_days;
      if (data.waiting_thresholds) { setThresholds(data.waiting_thresholds); setThDraft(data.waiting_thresholds); }
    }).catch(() => {});
  }, []);
  const saveThresholds = async () => {
    const o = parseInt(thDraft.orange, 10), r = parseInt(thDraft.red, 10);
    if (!o || o < 1 || !r || r <= o) { toast.error("Seuils invalides : orange ≥ 1 et rouge > orange."); return; }
    setThSaving(true);
    try {
      const { data } = await api.put("/admin/visio/settings", { cr_link_days: crDaysRef.current, waiting_orange_min: o, waiting_red_min: r });
      setThresholds(data.waiting_thresholds); setThDraft(data.waiting_thresholds);
      toast.success("Seuils d'alerte enregistrés");
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible")); } finally { setThSaving(false); }
  };

  // Rafraîchissement auto pour la salle d'attente (présence patient)
  useEffect(() => {
    if (!reload) return;
    const id = setInterval(() => reload(), 15000);
    return () => clearInterval(id);
  }, [reload]);

  // Ticker pour actualiser le compteur d'attente (« depuis X min »)
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Signal sonore + notification à l'arrivée d'un patient en salle d'attente
  const prevWaitingRef = useRef(new Set());
  const initedRef = useRef(false);
  const audioCtxRef = useRef(null);
  const [soundOn, setSoundOn] = useState(false);

  const playBeep = () => {
    try {
      const ac = audioCtxRef.current;
      if (!ac) return;
      if (ac.state === "suspended") ac.resume();
      const o = ac.createOscillator(); const g = ac.createGain();
      o.connect(g); g.connect(ac.destination);
      o.type = "sine"; o.frequency.value = 880; g.gain.value = 0.07;
      o.start(); setTimeout(() => { o.frequency.value = 1175; }, 130);
      setTimeout(() => { o.stop(); }, 300);
    } catch { /* audio indisponible */ }
  };

  const enableSound = async () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = audioCtxRef.current || new Ctx();
      if (audioCtxRef.current.state === "suspended") await audioCtxRef.current.resume();
      playBeep(); // bip de confirmation (débloque l'audio via le geste utilisateur)
      if ("Notification" in window && Notification.permission === "default") {
        await Notification.requestPermission().catch(() => {});
      }
      setSoundOn(true);
      toast.success("Alertes sonores activées");
    } catch { toast.error("Impossible d'activer le son dans ce navigateur"); }
  };

  useEffect(() => {
    const cur = new Set(teleconsults.filter((a) => a.patient_present && a.status !== "termine").map((a) => a.id));
    if (initedRef.current) {
      const arrivals = [...cur].filter((id) => !prevWaitingRef.current.has(id));
      if (arrivals.length) {
        playBeep();
        const first = teleconsults.find((a) => a.id === arrivals[0]);
        const nm = first?.name || "Un patient";
        toast.success(`${nm} vient d'entrer en salle d'attente`);
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Patient en salle d'attente", { body: `${nm} attend en téléconsultation` });
          }
        } catch { /* notif indisponible */ }
      }
    }
    prevWaitingRef.current = cur;
    initedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appts]);

  // Alerte visuelle : titre d'onglet clignotant quand un patient attend et que le médecin est sur un autre onglet
  useEffect(() => {
    const original = document.title;
    let interval = null;
    let flip = true;
    const tick = () => { document.title = flip ? "● Patient en attente" : original; flip = !flip; };
    const start = () => { if (!interval) { flip = true; interval = setInterval(tick, 1000); } };
    const stop = () => { if (interval) { clearInterval(interval); interval = null; } document.title = original; };
    const sync = () => { (waitingCount > 0 && document.hidden) ? start() : stop(); };
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => { document.removeEventListener("visibilitychange", sync); stop(); };
  }, [waitingCount]);

  const fmtDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const waitedLabel = (a) => {
    if (!a.patient_joined_at) return "";
    const mins = Math.max(0, Math.floor((Date.now() - new Date(a.patient_joined_at).getTime()) / 60000));
    return mins < 1 ? "à l'instant" : `depuis ${mins} min`;
  };
  const waitMins = (a) => a.patient_joined_at ? Math.max(0, Math.floor((Date.now() - new Date(a.patient_joined_at).getTime()) / 60000)) : 0;
  const waitStyle = (a) => {
    const m = waitMins(a);
    if (m >= thresholds.red) return { card: "border-red-300 bg-red-50", text: "text-red-700", start: "bg-red-600 hover:bg-red-700" };
    if (m >= thresholds.orange) return { card: "border-amber-300 bg-amber-50", text: "text-amber-700", start: "bg-amber-600 hover:bg-amber-700" };
    return { card: "border-emerald-200 bg-emerald-50/60", text: "text-emerald-700", start: "bg-emerald-600 hover:bg-emerald-700" };
  };

  const searchPatients = async () => {
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/visio/patients/search?q=${encodeURIComponent(query)}`);
      setResults(data.patients || []);
      if ((data.patients || []).length === 0) toast.info("Aucun patient trouvé — saisissez ses coordonnées ci-dessous");
    } catch (e) { toast.error(apiError(e)); } finally { setSearching(false); }
  };

  const pickPatient = (p) => {
    setForm((f) => ({ ...f, name: p.name || "", email: p.email || "", phone: p.phone || "", dob: p.dob || "" }));
    setResults([]);
    toast.success(`Patient sélectionné : ${p.name}`);
  };

  const create = async () => {
    if (!form.name || !form.email || !form.start) { toast.error("Nom, email et date/heure sont requis"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/visio/appointments", {
        type: "teleconsultation",
        start: new Date(form.start).toISOString(),
        duration_min: parseInt(form.duration_min) || 30,
        name: form.name, email: form.email, phone: form.phone, motif: form.motif,
      });
      toast.success(`Téléconsultation créée · lien envoyé par email${data.sms_mode === "réel" ? " et SMS" : " (SMS simulé)"}`);
      setForm(emptyForm); setQuery(""); reload && reload();
    } catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const startConsult = async (a) => {
    setStartingId(a.id);
    try {
      await api.post(`/admin/visio/appointments/${a.id}/start`);
      window.open(`/admin/visio/consultation/${a.id}`, "_blank", "noopener,noreferrer");
      reload && reload();
    } catch (e) { toast.error(apiError(e)); } finally { setStartingId(null); }
  };

  const resendLink = async (a) => {
    try {
      const { data } = await api.post(`/admin/visio/appointments/${a.id}/send-link`);
      toast.success(`Lien renvoyé${data.email_sent ? " · email" : ""}${data.sms_mode === "réel" ? " · SMS" : " · SMS simulé"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyLink = (a) => {
    navigator.clipboard.writeText(`${window.location.origin}/visio/${a.join_token}`);
    toast.success("Lien copié" + (a.pin ? ` · PIN ${a.pin}` : ""));
  };

  const openHistory = async (a) => {
    setHistory("loading");
    try {
      const params = a.email ? `email=${encodeURIComponent(a.email)}` : `phone=${encodeURIComponent(a.phone || "")}`;
      const { data } = await api.get(`/admin/visio/patients/history?${params}`);
      setHistory(data);
    } catch (e) { toast.error(apiError(e)); setHistory(null); }
  };

  const refreshCrLink = async (sessionId) => {
    try {
      const { data } = await api.post(`/admin/visio/appointments/${sessionId}/cr/refresh-link`);
      toast.success(`Nouveau lien du compte-rendu envoyé${data.email_sent ? " · email" : ""}${data.sms_mode === "réel" ? " · SMS" : " · SMS simulé"}`);
      if (history && (history.email || history.phone)) {
        const params = history.email ? `email=${encodeURIComponent(history.email)}` : `phone=${encodeURIComponent(history.phone)}`;
        const res = await api.get(`/admin/visio/patients/history?${params}`);
        setHistory(res.data);
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="teleconsult-manager">
      {/* Statistiques téléconsultation */}
      {stats?.teleconsult_stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="teleconsult-stats">
          {[
            { k: "taux_presence", label: "Taux de présence", val: `${stats.teleconsult_stats.taux_presence}%`, hint: `${stats.teleconsult_stats.presents}/${stats.teleconsult_stats.total} présents` },
            { k: "no_shows", label: "No-shows", val: stats.teleconsult_stats.no_shows, hint: "patients absents" },
            { k: "duree_moyenne_min", label: "Durée moyenne", val: `${stats.teleconsult_stats.duree_moyenne_min} min`, hint: `${stats.teleconsult_stats.termine} terminées` },
            { k: "total", label: "Téléconsultations", val: stats.teleconsult_stats.total, hint: "séances passées" },
          ].map((s) => (
            <div key={s.k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`teleconsult-stat-${s.k}`}>
              <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className="mt-1 font-display text-2xl font-bold text-navy">{s.val}</p>
              <p className="mt-0.5 text-[11px] text-slate-400">{s.hint}</p>
            </div>
          ))}
        </div>
      )}

      {/* Salle d'attente temps réel */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="teleconsult-waiting-room">
        <div className="mb-3 flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5"><span className={`absolute inline-flex h-full w-full rounded-full ${waitingCount ? "animate-ping bg-emerald-400 opacity-75" : "bg-slate-300"}`} /><span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${waitingCount ? "bg-emerald-500" : "bg-slate-300"}`} /></span>
          <h3 className="font-display text-base font-semibold text-ciel">Salle d'attente</h3>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">{waitingCount} présent{waitingCount > 1 ? "s" : ""}</span>
          <span className="ml-auto text-[11px] text-slate-400">Temps réel · actualisé toutes les 15 s</span>
        </div>
        {/* Seuils d'alerte réglables */}
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid="teleconsult-thresholds">
          <span className="text-xs font-medium text-slate-500">Seuils d'alerte (min) :</span>
          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
            <span className="h-2 w-2 rounded-full bg-amber-500" /> Orange
            <Input type="number" min={1} value={thDraft.orange}
              onChange={(e) => setThDraft((d) => ({ ...d, orange: e.target.value }))}
              className="h-7 w-16 px-2 text-center text-xs" data-testid="teleconsult-threshold-orange" />
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-red-700">
            <span className="h-2 w-2 rounded-full bg-red-500" /> Rouge
            <Input type="number" min={2} value={thDraft.red}
              onChange={(e) => setThDraft((d) => ({ ...d, red: e.target.value }))}
              className="h-7 w-16 px-2 text-center text-xs" data-testid="teleconsult-threshold-red" />
          </span>
          <Button size="sm" onClick={saveThresholds} disabled={thSaving}
            className="h-7 rounded-full bg-ciel px-3 text-xs text-white hover:bg-sky-700" data-testid="teleconsult-threshold-save">
            {thSaving ? <Loader2 size={12} className="animate-spin" /> : "Enregistrer"}
          </Button>
          <span className="text-[11px] text-slate-400" data-testid="teleconsult-threshold-current">Actuel : orange {thresholds.orange} min · rouge {thresholds.red} min</span>
        </div>
        {waitingCount === 0 ? (
          <p className="py-4 text-center text-sm text-slate-400" data-testid="teleconsult-waiting-empty">Aucun patient en attente pour le moment.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {waitingList.map((a) => {
              const ws = waitStyle(a);
              return (
              <div key={a.id} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${ws.card}`} data-testid={`teleconsult-waiting-card-${a.id}`}>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-navy">{a.name}</p>
                  <p className="text-xs text-slate-500">Prévu {fmtTime(a.start)}{a.motif ? ` · ${a.motif}` : ""}</p>
                  <p className={`mt-0.5 flex items-center gap-1 text-xs font-medium ${ws.text}`} data-testid={`teleconsult-waited-${a.id}`}><Clock size={11} /> En attente {waitedLabel(a)}</p>
                </div>
                <Button size="sm" className={`shrink-0 rounded-full text-xs text-white ${ws.start}`} onClick={() => startConsult(a)} disabled={startingId === a.id} data-testid={`teleconsult-waiting-start-${a.id}`}>
                  {startingId === a.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Play size={13} className="mr-1" />} Démarrer
                </Button>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Création */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="teleconsult-create">
        <div className="mb-4 flex items-center gap-2">
          <UserPlus size={18} className="text-ciel" />
          <h3 className="font-display text-base font-semibold text-ciel">Nouvelle téléconsultation</h3>
          <span className="ml-auto rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500" data-testid="teleconsult-integration-status">
            SMS : {stats?.sms_mode || "?"} · Email : actif · Daily.co : {stats?.daily_mode || "?"}
            {stats?.recording_enabled && (
              <span className="ml-1 text-emerald-600">· Enreg. {stats?.recording_auto ? "auto" : "manuel"}</span>
            )}
          </span>
        </div>

        {/* Recherche patient */}
        <Label className="text-xs text-slate-500">Rechercher un patient (nom, email, téléphone)</Label>
        <div className="mt-1.5 flex gap-2">
          <Input data-testid="teleconsult-search-input" value={query} placeholder="Ex : Awa, awa@… ou +225…"
            onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchPatients()} />
          <Button variant="outline" className="rounded-full" onClick={searchPatients} data-testid="teleconsult-search-btn">
            {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
          </Button>
        </div>
        {results.length > 0 && (
          <div className="mt-2 overflow-hidden rounded-lg border border-slate-200" data-testid="teleconsult-search-results">
            {results.map((p, i) => (
              <button key={i} onClick={() => pickPatient(p)} data-testid={`teleconsult-patient-${i}`}
                className="flex w-full items-center justify-between border-b border-slate-100 px-4 py-2 text-left text-sm last:border-0 hover:bg-slate-50">
                <span className="font-medium text-navy">{p.name}</span>
                <span className="text-xs text-slate-400">{p.email} {p.phone}</span>
              </button>
            ))}
          </div>
        )}

        {/* Coordonnées + planification */}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs text-slate-500">Nom du patient *</Label>
            <Input data-testid="teleconsult-name" className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Email *</Label>
            <Input data-testid="teleconsult-email" type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Téléphone (SMS)</Label>
            <Input data-testid="teleconsult-phone" className="mt-1.5" placeholder="+225…" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Motif</Label>
            <Input data-testid="teleconsult-motif" className="mt-1.5" value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Date & heure *</Label>
            <Input data-testid="teleconsult-start" type="datetime-local" className="mt-1.5" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Durée (min)</Label>
            <Input data-testid="teleconsult-duration" type="number" className="mt-1.5 font-mono" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} /></div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button className="rounded-full bg-ciel text-white hover:bg-sky-700" onClick={create} disabled={creating} data-testid="teleconsult-create-btn">
            {creating ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Video size={15} className="mr-1.5" />}
            Créer la salle & envoyer le lien
          </Button>
          {(form.name || form.email) && (
            <Button variant="ghost" size="sm" className="rounded-full text-slate-400" onClick={() => { setForm(emptyForm); setQuery(""); }} data-testid="teleconsult-clear">
              <X size={14} className="mr-1" /> Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Liste des téléconsultations */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-sm font-semibold text-slate-500">Téléconsultations programmées</h3>
        <Button variant={soundOn ? "default" : "outline"} size="sm"
          className={`rounded-full text-xs ${soundOn ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}`}
          onClick={enableSound} data-testid="teleconsult-sound-toggle">
          {soundOn ? <Volume2 size={14} className="mr-1.5" /> : <VolumeX size={14} className="mr-1.5" />}
          {soundOn ? "Alertes sonores activées" : "Activer les alertes sonores"}
        </Button>
      </div>
      {waitingCount > 0 && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800" data-testid="teleconsult-waiting-banner">
          <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" /></span>
          {waitingCount} patient{waitingCount > 1 ? "s" : ""} en salle d'attente — prêt{waitingCount > 1 ? "s" : ""} à démarrer
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="teleconsult-list">
        <table className="w-full min-w-[720px] text-sm">
          <thead><tr className="border-b border-slate-200 text-left font-mono text-[11px] uppercase tracking-wider text-slate-400">
            <th className="p-3">Date</th><th className="p-3">Heure</th><th className="p-3">Patient</th><th className="p-3">Statut salle</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {teleconsults.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">Aucune téléconsultation programmée.</td></tr>}
            {teleconsults.map((a) => {
              const badge = STATUS_BADGE[a.status] || STATUS_BADGE.confirme;
              const waiting = a.patient_present && a.status !== "termine";
              return (
                <tr key={a.id} className={`border-b border-slate-100 ${waiting ? "bg-emerald-50/50" : ""}`} data-testid={`teleconsult-row-${a.id}`}>
                  <td className="p-3 text-xs text-slate-500">{fmtDate(a.start)}</td>
                  <td className="p-3 font-mono text-xs text-navy">{fmtTime(a.start)}</td>
                  <td className="p-3"><span className="font-medium text-navy">{a.name}</span><br /><span className="text-xs text-slate-400">{a.email}{a.phone ? ` · ${a.phone}` : ""}</span></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.cls}`} data-testid={`teleconsult-badge-${a.id}`}>{badge.label}</span>
                      {waiting && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" data-testid={`teleconsult-waiting-${a.id}`}>
                          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                          En attente {waitedLabel(a)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {a.status !== "annule" && (
                        <Button size="sm" className="rounded-full bg-navy text-xs text-white hover:bg-navy-light" onClick={() => startConsult(a)} disabled={startingId === a.id} data-testid={`teleconsult-start-${a.id}`}>
                          {startingId === a.id ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Play size={13} className="mr-1" />}
                          {a.status === "termine" ? "Revoir" : "Démarrer"}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => resendLink(a)} data-testid={`teleconsult-resend-${a.id}`}>
                        <Send size={13} className="mr-1" /> Renvoyer le lien
                      </Button>
                      {a.join_token && (
                        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => copyLink(a)} data-testid={`teleconsult-copy-${a.id}`}>
                          <Copy size={13} className="mr-1" /> Copier
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => openHistory(a)} data-testid={`teleconsult-history-${a.id}`}>
                        <History size={13} className="mr-1" /> Historique
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-400"><Stethoscope size={13} /> Le patient rejoint directement depuis son navigateur — aucune installation requise.</p>

      {/* Dialog Historique patient */}
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="teleconsult-history-dialog">
          <DialogHeader><DialogTitle className="font-display text-navy">
            Historique patient{history && history !== "loading" && history.name ? ` — ${history.name}` : ""}
          </DialogTitle></DialogHeader>
          {history === "loading" ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-ciel" /></div>
          ) : history && history.sessions ? (
            history.sessions.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">Aucune téléconsultation passée.</p>
            ) : (
              <ul className="space-y-2">
                {history.sessions.map((s) => {
                  const b = STATUS_BADGE[s.status] || STATUS_BADGE.confirme;
                  return (
                    <li key={s.id} className="rounded-lg border border-slate-200 p-3" data-testid={`teleconsult-history-item-${s.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-navy">{new Date(s.start).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
                        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${b.cls}`}>{b.label}</span>
                      </div>
                      {s.motif && <p className="mt-1 text-xs text-slate-500">Motif : {s.motif}</p>}
                      {s.diagnostic && <p className="mt-0.5 text-xs text-slate-500">Diagnostic : {s.diagnostic}</p>}
                      {s.has_cr && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          {s.cr_expired ? (
                            <>
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500" data-testid={`teleconsult-history-expired-${s.id}`}>
                                <FileText size={11} /> Lien expiré
                              </span>
                              <button onClick={() => refreshCrLink(s.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-ciel hover:text-navy" data-testid={`teleconsult-history-refresh-${s.id}`}>
                                <Send size={12} /> Renouveler & renvoyer
                              </button>
                            </>
                          ) : s.cr_url ? (
                            <>
                              <a href={s.cr_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-ciel hover:text-navy" data-testid={`teleconsult-history-cr-${s.id}`}>
                                <FileText size={12} /> Compte-rendu PDF
                              </a>
                              {s.cr_expires_at && <span className="text-[11px] text-slate-400">valable jusqu'au {new Date(s.cr_expires_at).toLocaleDateString("fr-FR")}</span>}
                            </>
                          ) : null}
                          {s.has_cr && s.cr_access_count > 0 && (
                            <div className="w-full">
                              <button onClick={() => setLogOpen(logOpen === s.id ? null : s.id)} className="text-[11px] text-slate-400 underline-offset-2 hover:text-ciel hover:underline" data-testid={`teleconsult-history-access-${s.id}`}>
                                Consulté {s.cr_access_count} fois{s.cr_last_access ? ` · dernier accès le ${new Date(s.cr_last_access).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}` : ""} — voir le journal
                              </button>
                              {logOpen === s.id && (s.cr_access_log || []).length > 0 && (
                                <ul className="mt-1 space-y-0.5 rounded-md bg-slate-50 p-2" data-testid={`teleconsult-history-log-${s.id}`}>
                                  {s.cr_access_log.map((e, i) => (
                                    <li key={i} className="flex justify-between text-[11px] text-slate-500">
                                      <span>{new Date(e.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "medium" })}</span>
                                      {e.ip && <span className="font-mono text-slate-400">{e.ip}</span>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
